import axios, { AxiosError } from "axios";
import * as Sentry from "@sentry/react";
import qs from "qs";
import { ApiRoutes } from "../root/apiRoutes";
import { __ALLOW_DRAFT_MODE__ } from "../../polaris/globals";
import {
  EditModeBlockedError,
  getEditModeFlag,
  getShouldEnforceEditMode,
  onBlocked,
} from "../../polaris/edit-mode/editModeState";

import { CollectionGroupsController } from "./server/controller/CollectionGroupsController";
import { CookieScannerController } from "./server/controller/CookieScannerController";
import { WebsiteAuditController } from "./server/controller/WebsiteAuditController";
import { DataMapController } from "./server/controller/DataMapController";
import { DataRequestController } from "./server/controller/DataRequestController";
import { DataRetentionPolicyController } from "./server/controller/DataRetentionPolicyController";
import { GdprCookieConsentController } from "./server/controller/GdprCookieConsentController";
import { LoginController } from "./server/controller/LoginController";
import { OrganizationAuthController } from "./server/controller/OrganizationAuthController";
import { OrganizationController } from "./server/controller/OrganizationController";
import { OrganizationDataSourceController } from "./server/controller/OrganizationDataSourceController";
import { OrganizationUserController } from "./server/controller/OrganizationUserController";
import { OrganizationVendorController } from "./server/controller/OrganizationVendorController";
import { OrganizationManagedAdNetworkController } from "./server/controller/OrganizationManagedAdNetworkController";
import { OrgSurveyController } from "./server/controller/OrgSurveyController";
import { PolarisDataController } from "./server/controller/PolarisDataController";
import { PrivacyCenterController } from "./server/controller/PrivacyCenterController";
import { PrivacyNoticeController } from "./server/controller/PrivacyNoticeController";
import { ProcessingActivityController } from "./server/controller/ProcessingActivityController";
import { PublicOrganizationController } from "./server/controller/PublicOrganizationController";
import { RequestHandlingInstructionsController } from "./server/controller/RequestHandlingInstructionsController";
import { StorageController } from "./server/controller/StorageController";
import { TaskController } from "./server/controller/TaskController";
import { UserController } from "./server/controller/UserController";
import { VendorController } from "./server/controller/VendorController";
import { DataRecipientController } from "./server/controller/DataRecipientController";
import { DataProtectionAssessmentController } from "./server/controller/DataProtectionAssessmentController";
import { PrivacyCenterNoticeController } from "./server/controller/PrivacyCenterNoticeController";
import { PrivacyCenterNoticeSectionController } from "./server/controller/PrivacyCenterNoticeSectionController";
import { ChargebeeSessionController } from "./server/controller/ChargebeeSessionController";
import { MarketingWebsiteContentController } from "./server/controller/MarketingWebsiteContentController";
import { RoPAController } from "./server/controller/RoPAController";
import { TrueScanController } from "./server/controller/TrueScanController";
import { AuditLogController } from "./server/controller/AuditLogController";
import { OrganizationVendorClientController } from "./server/controller/OrganizationVendorClientController";
import { PostmarkInboxController } from "./server/controller/PostmarkInboxController";
import { OrganizationFileController } from "./server/controller/OrganizationFileController";
import { RamsayController } from "./server/controller/RamsayController";
import { ServiceAccountInboxController } from "./server/controller/ServiceAccountInboxController";

export const stringifyParams = (params) =>
  qs.stringify(params, {
    encode: false,
  });

const axiosInstance = axios.create({
  timeout: 60000,
  withCredentials: true,
  paramsSerializer: stringifyParams,
});

const READ_ONLY_ALLOWED_METHODS = new Set(["get", "head", "options"]);

const AUTH_ALLOWLISTED_PATHS = new Set([
  ApiRoutes.user.login,
  ApiRoutes.user.logout,
  ApiRoutes.user.session,
  ApiRoutes.user.forgotPassword,
  ApiRoutes.user.activeOrganization,
  ApiRoutes.mfa.root,
  ApiRoutes.mfa.validate,
  "/api/session",
]);

const AUTH_ALLOWLISTED_PREFIXES = ["/api/auth"];

const routePatternToRegExp = (route: string) =>
  new RegExp(`^${route.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/:[^/]+/g, "[^/]+")}$`);

const AUTH_ALLOWLISTED_ROUTE_PATTERNS = [
  routePatternToRegExp(ApiRoutes.user.activate),
  /^\/api\/users\/accept\/[^/]+$/,
  /^\/api\/users\/[^/]+$/,
];

const getPathname = (url?: string) => {
  if (!url) {
    return "";
  }

  try {
    return new URL(url, "http://localhost").pathname;
  } catch {
    return url.split("?")[0];
  }
};

const pathMatchesPrefix = (path: string, prefix: string) => {
  return path === prefix || path.startsWith(`${prefix}/`);
};

const isAuthAllowlisted = (url?: string) => {
  const path = getPathname(url);

  return (
    AUTH_ALLOWLISTED_PATHS.has(path) ||
    AUTH_ALLOWLISTED_PREFIXES.some((prefix) => pathMatchesPrefix(path, prefix)) ||
    AUTH_ALLOWLISTED_ROUTE_PATTERNS.some((pattern) => pattern.test(path))
  );
};

axiosInstance.interceptors.request.use((config) => {
  const method = (config.method ?? "get").toLowerCase();

  if (READ_ONLY_ALLOWED_METHODS.has(method)) {
    return config;
  }

  if (isAuthAllowlisted(config.url)) {
    return config;
  }

  if (!getShouldEnforceEditMode()) {
    return config;
  }

  if (getEditModeFlag()) {
    return config;
  }

  onBlocked();
  return Promise.reject(new EditModeBlockedError());
});

// Every /api endpoint returns JSON (enforced by JsonOnlyApiResponsesTest on the
// backend), so a 2xx /api response that axios resolves as a raw string means the
// body was not valid JSON — an HTML error/deploy page, a proxy or captive-portal
// interception, or a truncated body. Without this check, that garbage is recorded
// as a successful query, persisted to localStorage, and replayed on every boot,
// crash-looping the app until the user clears site data (POL-7616/POL-7654/POL-7688).
// Rejecting here keeps it out of the react-query cache entirely.
//
// Skipped for requests with a non-JSON responseType (blob/text downloads) and for
// logout, which intentionally follows Spring's redirect to the /login HTML page.
// Empty bodies are allowed on mutations (void handlers) but not on GETs.
axiosInstance.interceptors.response.use((response) => {
  const config = response.config ?? {};
  const jsonExpected = config.responseType === undefined || config.responseType === "json";
  const path = getPathname(config.url);
  const isApiPath = path.startsWith("/api") && path !== ApiRoutes.user.logout;

  if (jsonExpected && isApiPath && typeof response.data === "string") {
    const method = (config.method ?? "get").toLowerCase();
    if (response.data !== "" || method === "get") {
      const contentType = response.headers?.["content-type"] ?? "unknown";
      Sentry.captureMessage("Non-JSON 2xx response from API endpoint", {
        level: "warning",
        extra: {
          url: path,
          method,
          status: response.status,
          contentType,
          bodySnippet: response.data.slice(0, 200),
        },
      });
      return Promise.reject(
        new AxiosError(
          `Expected JSON from ${path} but received ${contentType}`,
          AxiosError.ERR_BAD_RESPONSE,
          config,
          response.request,
          response,
        ),
      );
    }
  }

  return response;
});

export type ApiMode = "Draft" | "Live";

// NOTE: This is disabled, and will ALWAYS use the live mode unless you run with `npm run transform:dev` instead of `npm run dev`
export const setApiMode = (apiMode: ApiMode, source: string) => {
  if (__ALLOW_DRAFT_MODE__) {
    if (apiMode == "Draft" || apiMode == "Live") {
      axiosInstance.defaults.headers.common["X-Api-Mode"] = apiMode;
    } else {
      console.warn(`Unrecognized api mode (${apiMode}) set from ${source}`);
    }
  } else {
    axiosInstance.defaults.headers.common["X-Api-Mode"] = "Live";
  }
};

export const getApiMode = (): ApiMode =>
  axiosInstance.defaults.headers.common["X-Api-Mode"] as ApiMode;

setApiMode("Live", "Api");

export const setBaseURL = (baseURL) => {
  axiosInstance.defaults.baseURL = baseURL;
};

export const Api = {
  authorization: new OrganizationAuthController(axiosInstance),
  consumerGroups: new CollectionGroupsController(axiosInstance),
  dataProtectionAssessment: new DataProtectionAssessmentController(axiosInstance),
  dataRecipient: new DataRecipientController(axiosInstance),
  dataRequest: new DataRequestController(axiosInstance),
  login: new LoginController(axiosInstance),
  organization: new OrganizationController(axiosInstance),
  organizationVendorClient: new OrganizationVendorClientController(axiosInstance),
  orgSurvey: new OrgSurveyController(axiosInstance),
  organizationUser: new OrganizationUserController(axiosInstance),
  organizationVendor: new OrganizationVendorController(axiosInstance),
  orgDataSource: new OrganizationDataSourceController(axiosInstance),
  polarisData: new PolarisDataController(axiosInstance),
  privacyCenter: new PrivacyCenterController(axiosInstance),
  privacyNotice: new PrivacyNoticeController(axiosInstance),
  notice: new PrivacyCenterNoticeController(axiosInstance),
  section: new PrivacyCenterNoticeSectionController(axiosInstance),
  requestHandlingInstructions: new RequestHandlingInstructionsController(axiosInstance),
  storage: new StorageController(axiosInstance),
  user: new UserController(axiosInstance),
  vendor: new VendorController(axiosInstance),
  tasks: new TaskController(axiosInstance),
  dataMap: new DataMapController(axiosInstance),
  dataRetention: new DataRetentionPolicyController(axiosInstance),
  processingActivities: new ProcessingActivityController(axiosInstance),
  cookieScanner: new CookieScannerController(axiosInstance),
  websiteAudit: new WebsiteAuditController(axiosInstance),
  trueScan: new TrueScanController(axiosInstance),
  gdprCookieConsent: new GdprCookieConsentController(axiosInstance),
  marketingWebsiteContent: new MarketingWebsiteContentController(axiosInstance),
  ropa: new RoPAController(axiosInstance),
  managedAdNetworks: new OrganizationManagedAdNetworkController(axiosInstance),
  postmarkInbox: new PostmarkInboxController(axiosInstance),
  ramsay: new RamsayController(),
  serviceAccountInbox: new ServiceAccountInboxController(axiosInstance),

  publicOrg: new PublicOrganizationController(axiosInstance),

  chargebeeSession: new ChargebeeSessionController(axiosInstance),
  auditLogs: new AuditLogController(axiosInstance),
  organizationFiles: new OrganizationFileController(axiosInstance),
};
